import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../auth/AuthPage/UserContext';
import Dashboard from './Dashboard';
import './HomePage.css';

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

function HomePage() {
    const { user, loading } = useUser();
    if (loading) return <div className="home-shell" />;
    return user ? <Dashboard user={user} /> : <Landing />;
}

/* ============================================================
   LANDING - logged-out visitors.
   Minimal text; a drifting river of real card art (cards up for
   trade first, then the most valuable printings) does the talking.
   Clicking any card runs a real search for it.
   ============================================================ */
function Landing() {
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [riverRows, setRiverRows] = useState([[], []]);
    const [query, setQuery] = useState('');
    const [nameHints, setNameHints] = useState([]);

    useEffect(() => {
        fetch('/api/stats')
            .then(r => (r.ok ? r.json() : null))
            .then(setStats)
            .catch(() => { });

        (async () => {
            try {
                const picks = [];
                const seenNames = new Set();
                const add = (card, forTrade) => {
                    if (!card?.image_path || seenNames.has(card.name)) return;
                    seenNames.add(card.name);
                    picks.push({ ...card, forTrade });
                };

                // Cards sitting in traders' public binders right now
                const forTrade = await fetch('/api/stats/for-trade')
                    .then(r => (r.ok ? r.json() : null)).catch(() => null);
                (forTrade?.cards || []).forEach(c => add(c, true));

                // Top up with the most valuable printings in the catalog
                const top = await fetch('/api/cards/search?sortBy=price-desc&limit=60')
                    .then(r => (r.ok ? r.json() : null)).catch(() => null);
                (top?.cards || []).forEach(c => add(c, false));

                const pool = picks.slice(0, 36);
                const mid = Math.ceil(pool.length / 2);
                setRiverRows([pool.slice(0, mid), pool.slice(mid)]);
            } catch { }
        })();
    }, []);

    // Lightweight autocomplete for the hero search box
    useEffect(() => {
        const q = query.trim();
        if (q.length < 3) {
            setNameHints([]);
            return;
        }
        const t = setTimeout(() => {
            fetch(`/api/card-names/search?q=${encodeURIComponent(q)}`)
                .then(r => (r.ok ? r.json() : []))
                .then(setNameHints)
                .catch(() => { });
        }, 250);
        return () => clearTimeout(t);
    }, [query]);

    const submitSearch = (e) => {
        e.preventDefault();
        if (query.trim()) {
            navigate('/search', { state: { cardName: query.trim() } });
        }
    };

    const renderRiverCard = (card, i) => (
        <button
            key={`${card.uuid}-${i}`}
            className="river-card"
            title={card.name}
            onClick={() => navigate('/search', { state: { cardName: card.name } })}
        >
            {/* eager: lazy-loading inside the CSS-animated river defers
                until user interaction, so cards showed up blank */}
            <img src={card.image_path} alt={card.name} loading="eager" decoding="async" />
            {card.forTrade ? (
                <span className="river-chip trade">for trade</span>
            ) : card.sortPrice != null ? (
                <span className="river-chip">{usd.format(card.sortPrice)}</span>
            ) : null}
        </button>
    );

    return (
        <div className="home-shell landing-v2">
            <section className="landing-hero">
                <h1 className="landing-title">The trading post for Magic collectors.</h1>
                <p className="landing-statline">
                    {stats ? `${stats.totalCards.toLocaleString()} cards` : 'Every card'} · live prices · free
                </p>
                <form className="hero-search" onSubmit={submitSearch}>
                    <input
                        type="text"
                        list="hero-card-names"
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        placeholder='Search any card… try "Lightning Bolt"'
                        aria-label="Search for a card"
                    />
                    <datalist id="hero-card-names">
                        {nameHints.map(n => <option value={n} key={n} />)}
                    </datalist>
                    <button type="submit" className="cta-gold">Search</button>
                </form>
                <button className="landing-join" onClick={() => navigate('/auth')}>
                    Join free &rarr;
                </button>
            </section>

            {riverRows[0].length > 3 && (
                <section className="card-river">
                    <div className="river-row">
                        <div className="river-track">
                            {[...riverRows[0], ...riverRows[0]].map(renderRiverCard)}
                        </div>
                    </div>
                    {riverRows[1].length > 3 && (
                        <div className="river-row river-reverse">
                            <div className="river-track">
                                {[...riverRows[1], ...riverRows[1]].map(renderRiverCard)}
                            </div>
                        </div>
                    )}
                </section>
            )}
        </div>
    );
}

/* ============================================================
   DASHBOARD - what logged-in users land on.
   ============================================================ */
export default HomePage;
